import { useAuth } from "@clerk/react"
import { useQueryClient } from "@tanstack/react-query"
import { useSessionMutation, useSessionQuery } from "../query/authenticated"
import { queryKeys } from "../query/keys"
import type { ClerkInvitationRecord } from "../schemas"
import {
  createClerkInvitationOnServer,
  fetchClerkInvitationsFromServer,
  revokeClerkInvitationOnServer,
} from "./remote"

function sortInvitations(records: ClerkInvitationRecord[]) {
  return [...records].sort((left, right) =>
    right.createdAt.localeCompare(left.createdAt),
  )
}

export function useInvitationsQuery() {
  const { userId } = useAuth()
  return useSessionQuery<ClerkInvitationRecord[]>({
    queryFn: ({ sessionToken, signal }) =>
      fetchClerkInvitationsFromServer(sessionToken, signal),
    queryKey: queryKeys.admin.invitations(userId),
  })
}

export function useCreateInvitationMutation() {
  const { userId } = useAuth()
  const queryClient = useQueryClient()
  const queryKey = queryKeys.admin.invitations(userId)

  return useSessionMutation({
    mutationFn: (emailAddress: string, { sessionToken }) =>
      createClerkInvitationOnServer(emailAddress, sessionToken),
    onSuccess: (created) => {
      queryClient.setQueryData<ClerkInvitationRecord[]>(queryKey, (previous) =>
        sortInvitations([created, ...(previous ?? [])]),
      )
    },
  })
}

export function useRevokeInvitationMutation() {
  const { userId } = useAuth()
  const queryClient = useQueryClient()
  const queryKey = queryKeys.admin.invitations(userId)

  return useSessionMutation({
    mutationFn: (invitationId: string, { sessionToken }) =>
      revokeClerkInvitationOnServer(invitationId, sessionToken),
    onSuccess: (revoked) => {
      queryClient.setQueryData<ClerkInvitationRecord[]>(queryKey, (previous) =>
        sortInvitations(
          (previous ?? []).map((record) =>
            record.id === revoked.id ? revoked : record,
          ),
        ),
      )
    },
  })
}
